import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { getAdminUsers } from "@/lib/api";
import Header from "@/components/Header";
import { colorForName, initialFromName } from "@/lib/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("es-AR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function SummaryCard({ label, value, color = "white", testId }) {
  return (
    <div
      className="border-4 border-[#101010] rounded-xl cm-shadow-sm p-4 md:p-5"
      style={{ background: color }}
      data-testid={testId}
    >
      <div className="font-mono-cm text-[10px] md:text-xs uppercase tracking-widest text-[#101010]/60">
        {label}
      </div>
      <div className="font-display text-3xl md:text-4xl tabular-nums leading-tight mt-1">
        {value}
      </div>
    </div>
  );
}

function UserAvatar({ user }) {
  const color = colorForName(user.name || user.email);
  const initial = initialFromName(user.name || user.email);

  if (user.picture) {
    return (
      <img
        src={user.picture}
        alt={user.name}
        referrerPolicy="no-referrer"
        className="w-9 h-9 rounded-full border-2 border-[#101010] shrink-0"
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
    );
  }

  return (
    <span
      className="w-9 h-9 rounded-full border-2 border-[#101010] flex items-center justify-center font-display text-sm shrink-0"
      style={{ background: color }}
    >
      {initial}
    </span>
  );
}

export default function AdminPage({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const response = await getAdminUsers();
        setData(response);
      } catch (_e) {
        toast.error("No se pudieron cargar los usuarios.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filteredUsers = useMemo(() => {
    if (!data?.users) return [];
    const term = query.trim().toLowerCase();
    if (!term) return data.users;
    return data.users.filter(
      (entry) =>
        entry.name?.toLowerCase().includes(term) ||
        entry.email?.toLowerCase().includes(term) ||
        entry.user_id?.toLowerCase().includes(term)
    );
  }, [data, query]);

  const activeUsers = useMemo(() => {
    if (!data?.users) return 0;
    return data.users.filter((entry) => entry.games_played > 0).length;
  }, [data]);

  return (
    <div className="min-h-screen px-4 py-6 md:px-6">
      <div className="max-w-6xl mx-auto">
        <Header user={user} current="admin" />

        <div className="border-4 border-[#101010] rounded-2xl cm-shadow bg-[#FFDE59] p-5 md:p-6 mb-6">
          <div className="font-mono-cm text-xs uppercase tracking-widest text-[#101010]/60">
            Panel interno
          </div>
          <h1 className="font-display text-3xl md:text-4xl uppercase tracking-tight mt-1">
            Usuarios registrados
          </h1>
          <p className="font-mono-cm text-xs md:text-sm uppercase tracking-widest text-[#101010]/70 mt-2">
            Solo visible para {user?.email}
          </p>
        </div>

        {loading ? (
          <div className="font-display text-2xl uppercase text-center py-16" data-testid="admin-users-loading">
            Cargando usuarios...
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <SummaryCard label="Total usuarios" value={data?.total ?? 0} testId="admin-total-users" />
              <SummaryCard
                label="Usuarios PRO"
                value={data?.pro_count ?? 0}
                color="#A8FF76"
                testId="admin-pro-users"
              />
              <SummaryCard
                label="Con partidas"
                value={activeUsers}
                color="#7DD3FC"
                testId="admin-active-users"
              />
            </div>

            <div className="border-4 border-[#101010] rounded-2xl cm-shadow bg-white overflow-hidden">
              <div className="p-4 md:p-5 border-b-4 border-[#101010] bg-[#FFFDF5]">
                <label className="block font-mono-cm text-xs uppercase tracking-widest text-[#101010]/60 mb-2">
                  Buscar
                </label>
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Nombre, email o ID..."
                  className="w-full border-2 border-[#101010] rounded-lg px-3 py-2 font-mono-cm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#FFDE59]"
                  data-testid="admin-search-input"
                />
              </div>

              <div className="overflow-x-auto">
                <Table data-testid="admin-users-table">
                  <TableHeader>
                    <TableRow className="border-b-4 border-[#101010] hover:bg-transparent">
                      <TableHead className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]">
                        Usuario
                      </TableHead>
                      <TableHead className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]">
                        Email
                      </TableHead>
                      <TableHead className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]">
                        Registro
                      </TableHead>
                      <TableHead className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]">
                        Plan
                      </TableHead>
                      <TableHead className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010] text-right">
                        Partidas
                      </TableHead>
                      <TableHead className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010] text-right">
                        Mejor
                      </TableHead>
                      <TableHead className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010] text-right">
                        Promedio
                      </TableHead>
                      <TableHead className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]">
                        Última actividad
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="py-10 text-center font-mono-cm text-sm uppercase tracking-widest text-[#101010]/60">
                          No hay usuarios para mostrar
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredUsers.map((entry) => (
                        <TableRow
                          key={entry.user_id}
                          className="border-b-2 border-[#101010]/10 hover:bg-[#FFFDF5]"
                          data-testid={`admin-user-row-${entry.user_id}`}
                        >
                          <TableCell>
                            <div className="flex items-center gap-3 min-w-[180px]">
                              <UserAvatar user={entry} />
                              <div>
                                <div className="font-display text-base leading-tight">{entry.name}</div>
                                <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/50 mt-0.5">
                                  {entry.user_id}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="font-mono-cm text-xs md:text-sm">{entry.email}</TableCell>
                          <TableCell className="font-mono-cm text-xs whitespace-nowrap">
                            {formatDate(entry.created_at)}
                          </TableCell>
                          <TableCell>
                            {entry.is_pro ? (
                              <span className="inline-flex font-mono-cm text-[10px] uppercase tracking-widest bg-[#FFDE59] border border-[#101010] px-2 py-0.5 rounded">
                                PRO
                              </span>
                            ) : (
                              <span className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/50">
                                Free
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="font-display text-lg text-right tabular-nums">
                            {entry.games_played}
                          </TableCell>
                          <TableCell className="font-display text-lg text-right tabular-nums">
                            {entry.best_score ?? "—"}
                          </TableCell>
                          <TableCell className="font-display text-lg text-right tabular-nums">
                            {entry.avg_score ?? "—"}
                          </TableCell>
                          <TableCell className="font-mono-cm text-xs whitespace-nowrap">
                            {formatDate(entry.last_active)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
